import type { ReactNode } from 'react';
import styles from '../GenerationWorkspace.module.css';
import { HierarchicalSelect, type HierarchicalOption, type RecommendedKeys } from '../../common/HierarchicalSelect';
import { CHARACTER_ETHNICITY } from '../../../features/actors/actorAttributes';

// 属性ラベルの前に出すアイコン (属性 key → svg と任意の回転角)。picker 有無に依らず適用。
const ATTRIBUTE_LABEL_ICONS: Record<string, { src: string; rotate?: number } | undefined> = {
  background: { src: '/stamps_image.svg' }, // 背景 → 画像
  angle: { src: '/camera_video.svg' }, // カメラアングル → ビデオカメラ
  shot: { src: '/composition_window_man.svg' }, // 被写体サイズ → 構図(人物)
  framing: { src: '/water_bottle_glass.svg' }, // 構図 → ボトル
  f_number: { src: '/aperture_thin.svg' }, // F値 → 絞り(アパーチャ)
  hand: { src: '/business_card_hand_2.svg' }, // 商品の配置 → 手
  scene: { src: '/yoga_half_moon_pose_1.svg' }, // シーン → ヨガ(ハーフムーン)
  seasons_weather: { src: '/weather_sun.svg' }, // 季節・気候・行事 → 太陽
  effect: { src: '/reward_stars_2.svg' }, // エフェクト → 星
  orientation: { src: '/water_bottle_glass.svg', rotate: 45 }, // 商品の向き → ボトルを右に45度倒す
  overall_impression: { src: '/shop_sale_1.svg' }, // 全体の雰囲気・印象 → ショップ
  expression: { src: '/face_id_1.svg' }, // 表情 → 顔
  fashion: { src: '/single_woman.svg' }, // ファッション → 人物
};

// 人物配下で HierarchicalSelect (ドロップダウン / モーダル) で描くフィールド。これ以外の group はボタン列。
// 体格 (build) / 身体的特徴 (appearance) は 2026-10-29 (migration 20261029_01) に「体格・身体的特徴」
// (physique) の配下へ移したが、migration 未適用の DB でも従来どおり動くよう残している。
const PERSON_SELECT_KEYS = new Set(['physique', 'build', 'appearance', 'expression', 'fashion']);
// 人種=キャラクターのとき隠す (キャラクターは体格・身体的特徴を持たない) フィールド
const PERSON_PHYSIQUE_KEYS = new Set(['physique', 'build', 'appearance']);

/** 推奨 (recommendedKeysByGroup) は「option の直近の親 group の key」で届くので、このセレクトが描く
 *  node 自身と子孫 group の分を合算する。体格・身体的特徴 > 体格 > option やファッション > 制服 > option
 *  のように入れ子になった group 配下の option にも「おすすめ」バッジ・並べ替えが効くようにする。 */
export function collectRecommendedKeys(
  node: AttributeNodeEditorProps['node'],
  byGroup: Record<string, RecommendedKeys> | undefined,
): RecommendedKeys | undefined {
  if (!byGroup) return undefined;
  const strong: string[] = [];
  const suggested: string[] = [];
  const visit = (n: AttributeNodeEditorProps['node']) => {
    const reco = byGroup[n.key];
    if (reco) {
      strong.push(...reco.strong);
      suggested.push(...reco.suggested);
    }
    for (const child of n.children || []) {
      if (child.children && child.children.length > 0) visit(child);
    }
  };
  visit(node);
  if (strong.length === 0 && suggested.length === 0) return undefined;
  return { strong, suggested };
}

// 人物のボタン型属性で、モバイル幅だけ差し替える短縮表示 (表示名 → 短縮名)。
// key ではなく表示名で引くので、パートナーが表示名を上書きした場合はそのまま出す。
const MOBILE_SHORT_OPTION_LABELS: Record<string, string | undefined> = {
  キャラクター: 'キャラ',
};

export type AttributeNodeEditorProps = {
  node: {
    id: string;
    key: string;
    label: string;
    type: 'group' | 'option' | 'numeric';
    metadata?: Record<string, unknown> | null;
    is_sensitive?: boolean;
    children?: AttributeNodeEditorProps['node'][];
  };
  value: unknown;
  onChange: (key: string, value: unknown) => void;
  parentKey?: string;
  /** 年代入力の placeholder / 指定時の初期値。省略時は 30。 */
  defaultAge?: number;
  modelsSection?: ReactNode;
  /** 商品AIプロファイル推奨 (計画書§5.2)。グループ key → strong/suggested の value 群。 */
  recommendedKeysByGroup?: Record<string, RecommendedKeys>;
};

export function AttributeNodeEditor({ node, value, onChange, parentKey, defaultAge = 30, modelsSection, recommendedKeysByGroup }: AttributeNodeEditorProps) {
  if (node.key === 'person') {
    const parentGroup = (value as Record<string, unknown> | undefined) || {};
    const children = node.children || [];
    // physique (体格・身体的特徴) ノードがツリーにあれば統合後。無ければ migration 前の DB で、
    // 体格 / 身体的特徴が person 直下に並ぶ従来の形
    const hasPhysiqueNode = children.some((child) => child.key === 'physique');
    const str = (v: unknown, fallback: string) => (typeof v === 'string' ? v : fallback);

    const groupValue: Record<string, unknown> & {
      gender: string;
      ethnicity: string;
      age: number | null;
    } = {
      gender: str(parentGroup.gender, 'none'),
      ethnicity: str(parentGroup.ethnicity, 'jp'),
      age: typeof parentGroup.age === 'number'
        ? parentGroup.age
        : (parentGroup.age === null ? null : defaultAge),
      expression: str(parentGroup.expression, 'none'),
      fashion: str(parentGroup.fashion, 'none'),
    };
    if (hasPhysiqueNode) {
      // 統合後: 旧キー (person.build / person.appearance) で保存された生成履歴・プリセットは
      // physique へ読み替える (体格を優先)。旧キーは groupValue に持たないので、次の変更で
      // 保存値からも消える (backend は旧キーのままでも子孫 group へ読み替えて描画する)
      const legacy = [parentGroup.build, parentGroup.appearance].find((v) => typeof v === 'string' && v !== 'none');
      groupValue.physique = str(parentGroup.physique, typeof legacy === 'string' ? legacy : 'none');
    } else {
      groupValue.build = str(parentGroup.build, 'none');
      groupValue.appearance = str(parentGroup.appearance, 'none');
    }

    // 人種=キャラクター (AI俳優キャラクター対応・俳優ウィザード step-1 と同じ扱い) は
    // 年齢・体格・身体的特徴を持たないため、それらの入力 UI を隠す (性別・表情・
    // ファッションはキャラクターにも意味があるため残す)。
    const isCharacterEthnicity = groupValue.ethnicity === CHARACTER_ETHNICITY;

    // 区切り線の上: 性別 / 人種 / 年代 (ボタン列・数値)。下: セレクト系 (ツリー順)
    const topChildren = children.filter((child) =>
      child.key !== 'none' && !PERSON_SELECT_KEYS.has(child.key) && !(isCharacterEthnicity && child.key === 'age'));
    const selectChildren = children.filter((child) =>
      PERSON_SELECT_KEYS.has(child.key) && !(isCharacterEthnicity && PERSON_PHYSIQUE_KEYS.has(child.key)));

    return (
      <>
      {/* 区切り線の上: 性別/人種/年代 + 俳優選択。媒体要件と同じ半々の2カラム */}
      <div className={`${styles.attributeGridNested} ${styles.attributePersonTop}`}>
        {topChildren.map((child) => (
          <AttributeNodeEditor
            key={child.id}
            node={child}
            value={groupValue[child.key]}
            onChange={(_, childValue) => {
              const nextGroup: typeof groupValue = { ...groupValue };

              if (child.key === 'age') {
                nextGroup.age = typeof childValue === 'number' ? childValue : null;
              } else if (child.key === 'gender') {
                nextGroup.gender = typeof childValue === 'string' ? childValue : nextGroup.gender;
              } else if (child.key === 'ethnicity') {
                nextGroup.ethnicity = typeof childValue === 'string' ? childValue : nextGroup.ethnicity;
                // キャラクターへ切り替えた場合は年齢・体格・身体的特徴をクリアする
                // (俳優ウィザード step-1 の handleEthnicityChange と同じ挙動)。
                if (nextGroup.ethnicity === CHARACTER_ETHNICITY) {
                  nextGroup.age = null;
                  for (const key of PERSON_PHYSIQUE_KEYS) {
                    if (key in nextGroup) nextGroup[key] = 'none';
                  }
                }
              }

              onChange(node.key, nextGroup);
            }}
            parentKey={node.key}
            defaultAge={defaultAge}
            recommendedKeysByGroup={recommendedKeysByGroup}
          />
        ))}
        {modelsSection}
      </div>
      {/* 区切り線の下: 体格・身体的特徴/表情/ファッション (モーダル・ドロップダウン)。コンテナ幅で列数が変わるグリッド */}
      <div className={`${styles.attributeGridNested} ${styles.attributePersonBottom}`}>
        {selectChildren.map((child) => (
          <AttributeNodeEditor
            key={child.id}
            node={child}
            value={groupValue[child.key]}
            onChange={(_, childValue) => {
              onChange(node.key, {
                ...groupValue,
                [child.key]: typeof childValue === 'string' ? childValue : groupValue[child.key],
              });
            }}
            parentKey={node.key}
            defaultAge={defaultAge}
            recommendedKeysByGroup={recommendedKeysByGroup}
          />
        ))}
      </div>
      </>
    );
  }

  if (node.type === 'numeric') {
    const meta = node.metadata || {};
    const min = typeof meta.min === 'number' ? meta.min : 10;
    const max = typeof meta.max === 'number' ? meta.max : 80;
    const numericValue = typeof value === 'number' ? value : undefined;
    if (node.key === 'age') {
      const isNone = value === null || value === undefined;
      const fallbackAge = Number.isFinite(defaultAge) ? defaultAge : 30;
      const currentAge = isNone ? fallbackAge : (typeof numericValue === 'number' && Number.isFinite(numericValue) ? numericValue : fallbackAge);

      return (
        <div className={`${styles.optionGroup} ${styles.attributeFieldWide}`}>
          <div className="gen-option-group-label">{node.label}</div>
          <div className={styles.optionGroupButtons}>
            <button
              type="button"
              className={`${styles.optionButton} ${isNone ? styles.optionButtonActive : ''}`}
              onClick={() => onChange(node.key, null)}
            >
              指定なし
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="number"
                min={min}
                max={max}
                value={isNone ? '' : currentAge}
                placeholder={String(fallbackAge)}
                onChange={(event) => {
                  const next = Number(event.target.value);
                  if (Number.isNaN(next)) {
                    return;
                  }
                  onChange(node.key, next);
                }}
                onFocus={() => {
                  if (isNone) {
                    onChange(node.key, fallbackAge);
                  }
                }}
                className={`${styles.ageInput} ${!isNone ? styles.ageInputActive : ''}`}
              />
              <span style={{ color: 'rgba(245, 241, 230, 0.7)', fontSize: '12px' }}>歳</span>
              <input
                type="range"
                min={min}
                max={max}
                value={currentAge}
                onChange={(event) => {
                  const next = Number(event.target.value);
                  if (!Number.isNaN(next)) {
                    onChange(node.key, next);
                  }
                }}
                className={`${styles.ageSlider} ${!isNone ? styles.ageSliderActive : ''}`}
              />
            </div>
          </div>
        </div>
      );
    }
    return (
      <label className="gen-label">
        {node.label}
        <div className={styles.numericInputRow}>
          <input
            type="number"
            min={min}
            max={max}
            value={typeof value === 'number' ? value : ''}
            onChange={(event) => {
              const next = Number(event.target.value);
              if (Number.isNaN(next)) {
                return;
              }
              onChange(node.key, next);
            }}
          />
          {typeof meta.unit === 'string' ? <span className={styles.unit}>{meta.unit}</span> : null}
        </div>
      </label>
    );
  }

  if (node.children && node.children.length) {
    const selectionType = typeof node.metadata?.selection === 'string' ? (node.metadata?.selection as string) : 'single';
    if (selectionType === 'group') {
      const groupValue = (value as Record<string, unknown> | undefined) || {};
      const filteredChildren = (node.children || []).filter((child) =>
        !(parentKey === 'person' && child.key === 'none')
      );

      return (
        <fieldset className={styles.fieldset}>
          <legend>{node.label}</legend>
          <div className={styles.attributeGridNested}>
            {filteredChildren.map((child) => (
              <AttributeNodeEditor
                key={child.id}
                node={child}
                value={groupValue[child.key]}
                      onChange={(_, childValue) => {
                  const nextGroup: Record<string, unknown> = {
                    ...groupValue,
                    [child.key]: childValue,
                  };
                  onChange(node.key, nextGroup);
                }}
                parentKey={node.key}
                defaultAge={defaultAge}
                recommendedKeysByGroup={recommendedKeysByGroup}
              />
            ))}
          </div>
        </fieldset>
      );
    }

    const selectedValue = typeof value === 'string' ? value : 'none';

    // 体格・身体的特徴 / 表情 / ファッション (PERSON_SELECT_KEYS) はモーダル・プルダウン形式、
    // それ以外の person 配下の属性 (性別 / 人種) はボタン形式
    if (parentKey === 'person' && !PERSON_SELECT_KEYS.has(node.key)) {
      const hasNoneOption = (node.children || []).some((child) => child.key === 'none');
      const normalizedChildren = hasNoneOption
        ? node.children || []
        : [
            {
              id: `${node.id}.none`,
              key: 'none',
              label: '指定なし',
              type: 'option' as const,
            },
            ...(node.children || []),
          ];

      return (
        <div className={styles.optionGroup}>
          <div className="gen-option-group-label">{node.label}</div>
          {/* 人種は 4 択でモバイル幅に 1 行で収まらないため、ボタンの余白・間隔を詰める */}
          <div className={`${styles.optionGroupButtons} ${node.key === 'ethnicity' ? styles.optionGroupButtonsCompact : ''}`}>
            {normalizedChildren.map((child) => {
              const optionKey = child.key;
              const optionId = child.id ?? `${node.id}.${optionKey}`;
              const isActive = selectedValue === optionKey;
              const shortLabel = MOBILE_SHORT_OPTION_LABELS[child.label];
              return (
                <button
                  key={optionId}
                  type="button"
                  className={`${styles.optionButton} ${isActive ? styles.optionButtonActive : ''} ${child.is_sensitive ? styles.optionSensitive : ''}`}
                  onClick={() => onChange(node.key, optionKey)}
                >
                  {shortLabel ? (
                    <>
                      <span className={styles.optionLabelFull}>{child.label}</span>
                      <span className={styles.optionLabelShort}>{shortLabel}</span>
                    </>
                  ) : child.label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    // このグループ自体が metadata.picker を持つ場合、グループごとモーダルに渡して
    // ビジュアルピッカー(配置グリッド/カメラアングル図)を描画する。
    // (picker は「options 内の入れ子グループ」で発火するため、自分自身を 1 要素の options として渡す)
    const hasPicker = typeof node.metadata?.picker === 'string';
    const labelIcon = ATTRIBUTE_LABEL_ICONS[node.key];
    // metadata.display='modal' のノード (体格・身体的特徴 / ファッション) は入れ子の有無に依らずモーダルで開く
    const forceModal = node.metadata?.display === 'modal';
    return (
      <HierarchicalSelect
        label={node.label}
        icon={labelIcon?.src}
        iconRotate={labelIcon?.rotate}
        options={hasPicker ? [node as unknown as HierarchicalOption] : node.children}
        value={selectedValue}
        onChange={(key) => onChange(node.key, key)}
        recommendedKeys={collectRecommendedKeys(node, recommendedKeysByGroup)}
        forceModal={forceModal}
      />
    );
  }

  if (node.type === 'option') {
    const isSelected = value === node.key;
    return (
      <label className={styles.optionLabel}>
        <input
          type="radio"
          name={node.id}
          checked={isSelected}
          onChange={() => onChange(node.key, node.key)}
        />
        <span>{node.label}</span>
      </label>
    );
  }

  return null;
}
